/*
 * Painted Idols — de bewegingslaag, JavaScript-deel.
 *
 * Alleen wat CSS niet kan: de cursor die een label draagt, het scramble-effect
 * op menu-items en getallen, en het opsplitsen van koppen in woorden. De
 * scroll-effecten, de rail en de paginaovergang zijn CSS (pi-motion.css).
 *
 * Budget uit INTERACTIE.md §8.5: de hele bewegingslaag onder 40 kB. Dit bestand
 * hoort daar het grootste deel van te laten liggen voor het WebGL-doek, dat pas
 * op de productpagina laadt.
 *
 * Alles hieronder is opt-in via data-attributen, zodat een sjabloon zelf bepaalt
 * wat meedoet. Valt dit bestand weg, dan blijft de pagina compleet.
 */
(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;


  /* -------------------------------------------------------------
   * §3.2 Koppen komen per woord binnen.
   * We splitsen op woord, niet op letter. De CSS doet de beweging; hier
   * zetten we alleen de volgorde. Bestaande opmaak in de kop blijft heel
   * doordat we per tekstknoop werken in plaats van innerHTML te herschrijven.
   * ----------------------------------------------------------- */
  function splitWords(el) {
    if (el.dataset.piSplit === 'done') return;
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    let i = 0;
    for (const node of nodes) {
      if (!node.nodeValue.trim()) continue;
      const frag = document.createDocumentFragment();
      for (const part of node.nodeValue.split(/(\s+)/)) {
        if (!part) continue;
        if (/^\s+$/.test(part)) {
          frag.appendChild(document.createTextNode(part));
          continue;
        }
        const span = document.createElement('span');
        span.className = 'pi-w';
        span.style.setProperty('--pi-i', i++);
        span.textContent = part;
        frag.appendChild(span);
      }
      node.parentNode.replaceChild(frag, node);
    }
    el.classList.add('pi-words');
    el.dataset.piSplit = 'done';
  }

  /* -------------------------------------------------------------
   * §3.1 Scramble.
   * Alleen op menu-items bij hover en op getallen die binnenkomen — nooit op
   * lopende tekst. De breedte mag niet verspringen, dus we vervangen letter
   * voor letter en houden spaties waar ze staan.
   * ----------------------------------------------------------- */
  const GLYPHS = '#$%&/\\<>*+=-_[]{}0123456789';

  /* -------------------------------------------------------------
   * The page head arrives in two steps (Willem, 29-09-2026: variant C, then D).
   * First the words come into focus one after another, then the paint stroke
   * under the stress is drawn from left to right. Both are CSS animations in
   * pi-motion.css; this only starts them and times the stroke after the words.
   * ----------------------------------------------------------- */
  function arriveHead(el) {
    const words = el.querySelectorAll('.pi-w');
    if (!words.length) return;
    el.style.setProperty('--pi-stroke-at', `${words.length * 45 + 450}ms`);
    el.classList.add('pi-head-in');
  }

  function scramble(el, duration = 220, done) {
    if (reduced || el.dataset.piBusy === '1') {
      if (done) done();
      return;
    }
    const target = el.dataset.piText || el.textContent;
    el.dataset.piText = target;
    el.dataset.piBusy = '1';

    const start = performance.now();
    const chars = [...target];

    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const settled = Math.floor(p * chars.length);
      el.textContent = chars
        .map((c, i) => {
          if (i < settled || /\s/.test(c)) return c;
          return GLYPHS[(Math.random() * GLYPHS.length) | 0];
        })
        .join('');
      if (p < 1) {
        requestAnimationFrame(tick);
      } else {
        el.textContent = target;
        el.dataset.piBusy = '0';
        if (done) done();
      }
    };
    requestAnimationFrame(tick);
  }

  /* -------------------------------------------------------------
   * §2 De cursor.
   * Een ring die de muis met naloop volgt (lerp 0.15 per frame). Boven een
   * klikbaar blok groeit hij tot een schijf met tekst erin: see this canvas,
   * drag, read, turn it, hang it. Bestaat niet op touch.
   *
   * Twee dingen die de CSS niet kan en die daarom hier staan:
   *
   * 1. Welke ondergrond eronder ligt. De ring hangt aan <body> en staat dus
   *    buiten .pi-dark en .pi-sheet; een afstammelingsselector kan hem niet
   *    bereiken. closest() leest de dichtstbijzijnde van de twee af en zet het
   *    antwoord in data-on, waarna pi-motion.css de kleuren omkeert. Staan ze
   *    genest, dan wint vanzelf de binnenste — dat is wat je onder de muis
   *    ziet.
   * 2. Waar het label hangt. Gecentreerd op de punt valt het over de link waar
   *    je op staat, dus het hangt rechtsonder. Bij een link rechtsonder in
   *    beeld zou het buiten het venster vallen: dan klapt het naar de andere
   *    kant. De kale ring blijft wél om de punt heen staan.
   * ----------------------------------------------------------- */
  const CUR_DX = 20; // het label staat zoveel rechts van de punt
  const CUR_DY = 16; // en zoveel eronder
  const CUR_EDGE = 10; // en nooit dichter dan dit op de vensterrand

  function initCursor() {
    if (!fine || reduced) return;

    const ring = document.createElement('div');
    ring.className = 'pi-cursor';
    ring.setAttribute('aria-hidden', 'true');
    ring.dataset.label = '';
    ring.dataset.on = 'dark';
    document.body.appendChild(ring);

    let aimX = innerWidth / 2;
    let aimY = innerHeight / 2;
    let curX = aimX;
    let curY = aimY;

    // De maat is nodig om binnen het venster te blijven, maar wordt alleen
    // gelezen als het label verandert. Elke frame meten is elke frame een
    // gedwongen layout, en op de collectiepagina staan tweeëndertig doeken.
    let boxW = 26;
    let boxH = 26;
    let remeasure = true;

    // Wat ligt er onder de punt? Eén functie, want hetzelfde antwoord bepaalt
    // het label én de kleur.
    const readUnder = (target) => {
      const hit = target && target.closest('[data-pi-cursor]');
      const label = hit ? hit.dataset.piCursor : '';

      if (label !== ring.dataset.label) {
        ring.dataset.label = label;
        ring.textContent = label;
        remeasure = true;
      }

      const surface = target && target.closest('.pi-sheet, .pi-dark');
      ring.dataset.on = surface && surface.classList.contains('pi-sheet') ? 'light' : 'dark';
    };

    addEventListener(
      'pointermove',
      (e) => {
        aimX = e.clientX;
        aimY = e.clientY;
        readUnder(e.target instanceof Element ? e.target : null);
      },
      { passive: true }
    );

    // De pagina kan onder een stilstaande muis door schuiven. Dan wisselt de
    // ondergrond zonder dat er een pointermove komt, en zou een witte ring op
    // een wit vel blijven staan — precies wat dit moest oplossen. Eén hittest
    // per frame waarin gescrold wordt, niet per scroll-gebeurtenis, want een
    // hittest dwingt layout af.
    //
    // capture, want vanaf 990px scrollt .page-wrapper en niet het document
    // (base.css zet daar overflow: hidden op html), en een scroll-gebeurtenis
    // van een element borrelt niet op. In de capture-fase komt hij wel langs.
    let hitPending = false;
    addEventListener(
      'scroll',
      () => {
        if (hitPending) return;
        hitPending = true;
        requestAnimationFrame(() => {
          hitPending = false;
          readUnder(document.elementFromPoint(aimX, aimY));
        });
      },
      { passive: true, capture: true }
    );

    const frame = () => {
      curX += (aimX - curX) * 0.15;
      curY += (aimY - curY) * 0.15;

      if (remeasure) {
        boxW = ring.offsetWidth;
        boxH = ring.offsetHeight;
        remeasure = false;
      }

      let x;
      let y;

      if (ring.dataset.label) {
        x = curX + CUR_DX;
        y = curY + CUR_DY;
        // Loopt hij het venster uit, dan naar de andere kant van de punt.
        if (x + boxW > innerWidth - CUR_EDGE) x = curX - CUR_DX - boxW;
        if (y + boxH > innerHeight - CUR_EDGE) y = curY - CUR_DY - boxH;
        // En als hij aan beide kanten niet past: tegen de rand, niet erbuiten.
        x = Math.min(Math.max(x, CUR_EDGE), Math.max(CUR_EDGE, innerWidth - CUR_EDGE - boxW));
        y = Math.min(Math.max(y, CUR_EDGE), Math.max(CUR_EDGE, innerHeight - CUR_EDGE - boxH));
      } else {
        x = curX - boxW / 2;
        y = curY - boxH / 2;
      }

      ring.style.transform = `translate(${x}px, ${y}px)`;
      requestAnimationFrame(frame);
    };
    requestAnimationFrame(frame);
  }

  /* -------------------------------------------------------------
   * Kantelen volgt de muis.
   * Het doek springt niet naar één vaste hoek maar draait mee met waar de
   * cursor staat: links van het midden kantelt hij de ene kant op, rechts de
   * andere. Subtiel — een paar graden — want het moet lezen als een object dat
   * je scheef houdt, niet als een draaideur.
   *
   * Eén listener op de container in plaats van één per doek: op de
   * collectiepagina staan er tweeëndertig.
   * ----------------------------------------------------------- */
  const TILT_Y = 26; // graden links-rechts
  const TILT_X = 17; // graden boven-onder

  function initTilt() {
    if (!fine || reduced) return;

    const setTilt = (stage, e) => {
      const obj = stage.querySelector('.pi-obj');
      if (!obj) return;
      const box = stage.getBoundingClientRect();
      const nx = (e.clientX - box.left) / box.width - 0.5; // -0.5 .. 0.5
      const ny = (e.clientY - box.top) / box.height - 0.5;
      obj.style.setProperty('--pi-ry', (nx * TILT_Y * 2).toFixed(2) + 'deg');
      obj.style.setProperty('--pi-rx', (-ny * TILT_X * 2).toFixed(2) + 'deg');
    };

    const clearTilt = (stage) => {
      const obj = stage.querySelector('.pi-obj');
      if (!obj) return;
      obj.style.setProperty('--pi-ry', '0deg');
      obj.style.setProperty('--pi-rx', '0deg');
    };

    document.addEventListener(
      'pointermove',
      (e) => {
        const stage = e.target.closest('.pi-stage');
        if (stage) setTilt(stage, e);
      },
      { passive: true }
    );

    document.addEventListener(
      'pointerout',
      (e) => {
        const stage = e.target.closest('.pi-stage');
        if (stage && !stage.contains(e.relatedTarget)) clearTilt(stage);
      },
      { passive: true }
    );
  }

  /* -------------------------------------------------------------
   * Een doek draaien met je vinger.
   *
   * Op een muis doet aanwijzen twee dingen: het zet het doek recht (de
   * :hover-regel in pi.css) en het kantelt mee met de cursor (initTilt
   * hierboven). Op een telefoon bestaat aanwijzen niet, en dan kon een doek op
   * de wand en op de homepage alleen bewegen door te scrollen -- de stand hing
   * volledig aan de scrollpositie en was zelf niet aan te raken.
   *
   * Slepen doet hier daarom allebei: het zet het doek recht (dezelfde reset als
   * hover) en het draait het onder je vinger door. Loslaten laat het staan en
   * na anderhalve seconde geeft het de scrollrotatie weer terug -- dezelfde
   * maat als de productpagina, zodat de twee zich hetzelfde gedragen.
   *
   * Alleen voor touch en pen: met een muis is er hover, en twee mechanismen op
   * één gebaar vechten met elkaar.
   *
   * Vier dingen zaten er verkeerd in, en alle vier zijn ze gemeten in een
   * nabouw met deze CSS en dit script erin (scripts/shopify/drag-nabouw.mjs):
   *
   * 1. Er stond geen `touch-action`. Nergens in het thema, gemeten: elke
   *    `.pi-stage` en het kader op de productpagina stonden op `auto`. Dan
   *    leest de browser een veeg als scrollen, neemt het gebaar over en stuurt
   *    `pointercancel` -- en daarna komt er geen `pointermove` meer. Op een
   *    telefoon is dat het einde van de sleep voordat hij begonnen is. De
   *    `touch-action` staat nu in pi-motion.css: `pan-y`, dus verticaal blijft
   *    scrollen en horizontaal en diagonaal draaien het doek.
   *
   * 2. Een afgebroken gebaar werd behandeld als een losgelaten gebaar. Gemeten:
   *    na een `pointercancel` stond het doek anderhalve seconde vastgezet op de
   *    hoek waar je vinger was, met de scrollrotatie op pauze, en sprong het
   *    daarna terug. Dat is wat je op de homepage zag als een carrousel die
   *    niet meer wil: elke veeg over een doek zette dat ene doek stil. Afbreken
   *    laat nu meteen los, net als op de productpagina.
   *
   * 3. In een rail hoort de horizontale veeg aan de rail. Dat gebaar kan niet
   *    twee dingen betekenen, en de doeken daar draaien al mee met de
   *    scrollpositie (pi-turn3d op animation-timeline: view()).
   *
   *    Op 02-09-2026 stond deze sleep daarom binnen [data-pi-rail] uit, Willem
   *    meldde dat de rotatie op de homepage "helemaal niet" werkte, en die
   *    keuze heb ik hem toen als mededeling gerapporteerd in plaats van als
   *    vraag. Voorgelegd koos hij een derde weg: de veeg bedient de carrousel
   *    én het doek draait mee met waar het staat. Dat staat nu in
   *    initRailTurn, dus de sleep is hier weer uit -- niet omdat het gebaar
   *    ergens anders heen moet, maar omdat er geen gebaar meer nodig is.
   *
   *    Op de wand blijft de sleep wél aan, met beide assen: daar is geen
   *    carrousel die om de beweging vecht. Zie
   *    sections/pi-collection-wall.liquid voor de touch-action daar.
   *
   * 4. `.pi-turn` is op de wand een <a>. Zonder drempel begon een tik al een
   *    sleep, en met de pointer-overname zou de klik op de stage landen in
   *    plaats van op de link -- precies de fout die initRail hieronder al eens
   *    heeft gemaakt. Onder de zes pixels is het daarom nog een tik; daarboven
   *    nemen we de pointer over en slikken we de klik die erop volgt.
   *
   * Eén luisteraar op het document en niet één per doek: op de collectiepagina
   * staan tweeëndertig stages, en een stage die later in beeld komt (de theme
   * editor rendert een sectie opnieuw) deed met de oude opzet niet mee.
   * ----------------------------------------------------------- */
  const DRAG_SLOP = 6; // pixels voordat een tik een sleep wordt
  const DRAG_GAIN = 0.25; // graden per gesleepte pixel, gelijk aan de productpagina
  const DRAG_MAX_Y = 42; // graden links-rechts, de uiterste stand
  /* En boven-onder. Deze as was er niet: de sleep schreef alleen rotateY, dus op
   * een telefoon draaide een doek op de wand alleen horizontaal terwijl hij op
   * een muis met initTilt allebei de kanten op kantelde (Willem, 02-09-2026).
   *
   * 27 graden en niet 42, in de verhouding die de rest van het thema aanhoudt:
   * initTilt hierboven geeft 26 links-rechts en 17 boven-onder, dus
   * 42 x 17 / 26 = 27,5. Boven-onder mag minder ver, en dat is geen smaak --
   * een doek is staand, dus bij dezelfde hoek steekt de bovenzijkant verder uit
   * dan de zijzijkant. Dezelfde afweging staat in sections/pi-product.liquid.
   *
   * De versterking is voor beide assen gelijk (DRAG_GAIN). Dat is het punt van
   * diagonaal: een sleep van 45 graden geeft dan even veel draaiing als
   * kanteling en volgt de richting van je hand. */
  const DRAG_MAX_X = 27; // graden boven-onder
  const DRAG_HOLD = 1500; // ms dat de stand blijft staan na loslaten

  function initStageDrag() {
    if (reduced) return;

    let stage = null; // de stage waar dit gebaar op begon
    let turn = null;
    let startX = 0;
    let startY = 0;
    let moved = false; // is de drempel gehaald?
    let terug = 0;

    /**
     * Zet het doek op een stand en houdt het daar.
     * Gebruikt dezelfde twee eigenschappen als de :hover-regel in pi.css, zodat
     * de muis- en de vingerweg niet uit elkaar lopen.
     *
     * rotateY vóór rotateX, en niet omgekeerd: transformaties zijn niet
     * verwisselbaar. In deze volgorde blijft links-rechts links-rechts ten
     * opzichte van het scherm, ook als het doek al gekanteld staat; in de andere
     * volgorde draait een horizontale sleep het doek om zijn eigen scheve as en
     * voelt diagonaal slepen als tollen. Dezelfde volgorde als op de
     * productpagina.
     * @param {number} dx gesleepte pixels links-rechts
     * @param {number} dy gesleepte pixels boven-onder
     */
    const zet = (dx, dy) => {
      const ry = Math.max(-DRAG_MAX_Y, Math.min(DRAG_MAX_Y, dx * DRAG_GAIN));
      const rx = Math.max(-DRAG_MAX_X, Math.min(DRAG_MAX_X, dy * DRAG_GAIN));
      turn.style.setProperty('animation-play-state', 'paused');
      turn.style.setProperty(
        'transform',
        'rotateY(' + ry.toFixed(1) + 'deg) rotateX(' + rx.toFixed(1) + 'deg)',
        'important'
      );
      turn.style.setProperty('rotate', 'y 0deg');
    };

    /**
     * Geeft het doek terug aan de scrollrotatie.
     * @param {HTMLElement} el het element dat de rotatie draagt
     */
    const los = (el) => {
      el.style.removeProperty('animation-play-state');
      el.style.removeProperty('transform');
      el.style.removeProperty('rotate');
    };

    /** Vergeet het lopende gebaar zonder de stand aan te raken. */
    const vergeet = () => {
      stage = null;
      turn = null;
      moved = false;
    };

    document.addEventListener(
      'pointerdown',
      (e) => {
        if (e.pointerType === 'mouse') return; // met een muis is er hover
        if (!(e.target instanceof Element)) return;

        const hit = e.target.closest('.pi-stage');
        if (!hit) return;
        /* In een rail niet. Willem heeft die keuze op 02-09-2026 gemaakt: de
         * veeg bedient de carrousel, en de rotatie komt uit de plek van het
         * doek in de rail (initRailTurn hierboven). Twee mechanismen op één
         * gebaar vechten met elkaar, en nu is er geen gebaar meer nodig -- het
         * doek draait al terwijl het langskomt. */
        if (hit.closest('[data-pi-rail]')) return;

        const rot = hit.querySelector('.pi-turn');
        if (!rot) return; // een print heeft .pi-paper en draait niet

        stage = hit;
        turn = rot;
        startX = e.clientX;
        startY = e.clientY;
        moved = false;
        clearTimeout(terug);
        // Blijft de vlag van een vorige sleep staan -- een sleep hoeft geen
        // klik op te leveren -- dan zou de volgende tik geslikt worden.
        delete hit.dataset.piDragged;
      },
      { passive: true }
    );

    document.addEventListener(
      'pointermove',
      (e) => {
        if (!stage) return;
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        if (!moved) {
          // Over de afstand en niet over dx alleen: een verticale sleep haalde
          // de drempel anders nooit, en dan bestond de tweede as feitelijk niet.
          if (Math.abs(dx) < DRAG_SLOP && Math.abs(dy) < DRAG_SLOP) return; // nog een tik
          moved = true;
          // Pas nu de pointer overnemen. Eerder zou de klik op de stage landen
          // in plaats van op de <a> eromheen, en dan is geen doek meer aan te
          // tikken.
          try {
            stage.setPointerCapture(e.pointerId);
          } catch (err) {
            // Geen actieve pointer (kan bij een afgebroken gebaar): dan sleept
            // hij zonder overname verder, en dat werkt ook.
          }
          // Onthoud op de stage zelf dat er gesleept is. De klik komt na
          // pointerup en dan is het gebaar al vergeten, dus een variabele
          // helpt daar niet.
          stage.dataset.piDragged = '1';
          zet(0, 0); // eerst recht, zoals aanwijzen op een muis doet
        }

        zet(dx, dy);
      },
      { passive: true }
    );

    // Loslaten laat de stand staan; na DRAG_HOLD neemt de scrollrotatie het
    // weer over. Zo kun je de zijkant rustig bekijken.
    document.addEventListener(
      'pointerup',
      () => {
        if (!stage) return;
        const el = turn;
        const was = moved;
        vergeet();
        if (!was) return; // een tik heeft niets veranderd
        clearTimeout(terug);
        terug = setTimeout(() => los(el), DRAG_HOLD);
      },
      { passive: true }
    );

    /* Afgebroken is niet hetzelfde als losgelaten. `pointercancel` betekent dat
     * de browser het gebaar heeft overgenomen -- op een telefoon is dat de
     * pagina die gaat scrollen. Dan is er niets om te bekijken en hoort het
     * doek meteen terug, niet anderhalve seconde vastgezet te blijven staan.
     * Dezelfde regel als op de productpagina. */
    document.addEventListener(
      'pointercancel',
      () => {
        if (!stage) return;
        const el = turn;
        const was = moved;
        vergeet();
        clearTimeout(terug);
        if (was) los(el);
      },
      { passive: true }
    );

    // Na een echte sleep hoort de klik die de browser er alsnog achteraan
    // stuurt niet te navigeren -- anders opent slepen het doek dat je stond te
    // bekijken. In de capture-fase, zodat de <a> hem niet eerst te pakken
    // krijgt.
    document.addEventListener(
      'click',
      (e) => {
        if (!(e.target instanceof Element)) return;
        const hit = e.target.closest('.pi-stage');
        if (!hit || hit.dataset.piDragged !== '1') return;
        delete hit.dataset.piDragged;
        e.preventDefault();
        e.stopPropagation();
      },
      true
    );
  }

  /* -------------------------------------------------------------
   * De rail: het doek draait mee met waar het staat.
   *
   * Willem, 02-09-2026: "kunnen we de rotatie van de schilderijen dan koppelen
   * aan de verplaatsing van de doeken van links naar rechts. zodat je wel ziet
   * dat de doeken roteren. (niet alleen horizontaal)."
   *
   * Dus: de veeg bedient de carrousel, en de plek van een doek in de rail
   * bepaalt zijn stand. De sleep op het doek zelf is hier daarom uit -- zie
   * initStageDrag -- want dat gebaar is nu van de rail.
   *
   * Waarom dit in JavaScript staat en niet in CSS, waar het stond:
   *
   * 1. Het stond op `animation-timeline: view(inline)`, en dat is een
   *    scroll-driven animation. Die ondersteuning is jong. Willem ziet de
   *    rotatie wél op desktop en niet op mobiel, en dat is het patroon van een
   *    ontbrekende eigenschap en niet van een kapotte handler -- een gebroken
   *    listener faalt overal. Zonder terugval staat het doek op zo'n telefoon
   *    simpelweg stil.
   * 2. Ik kan het niet meten. In de nabouw bestaat de `ViewTimeline`, staat de
   *    animatie op `running`, en geeft `timeline.currentTime` op élke
   *    scrollpositie `null` -- een verborgen tab laat een view timeline niet
   *    oplossen. Twee routes waarvan ik er één niet kan nakijken is hoe dit
   *    gebrek is blijven staan.
   *
   * Eén route dus, en die loopt op de hoofdthread in plaats van op de
   * compositor. Dat is wat het kost: op een lange rail met vierentwintig doeken
   * is dit werk per scrollframe. Daarom één rAF per scroll-gebeurtenis en niet
   * per gebeurtenis rekenen, en alleen schrijven wat verandert.
   *
   * De luisteraar hangt aan de rail zelf. Niet aan window en niet aan
   * .page-wrapper: de rail is zijn eigen horizontale scroller, en een
   * scroll-gebeurtenis van een element bubbelt niet (zie initHeader).
   * ----------------------------------------------------------- */
  const RAIL_Y = 52; // graden links-rechts aan de uiterste kant
  /* De tweede en derde as. Dit is Willems "niet alleen horizontaal": de opzet
   * deed alleen rotateY, en dan zwenkt een doek als een deur in plaats van te
   * kantelen als een object.
   *
   * De verhouding komt van pi-turn3d hierboven, dat 34 / 10 / 2,5 aanhoudt --
   * dus X op 29,4% van Y en Z op 7,4%. Boven-onder mag minder ver dan
   * links-rechts, want een doek is staand: bij dezelfde hoek steekt de
   * bovenzijkant verder uit dan de zijzijkant.
   *
   * Willem, 02-09-2026: "op de home mag de hoek groter, waardoor de rotatie
   * meer opvalt." Van 38 / 12 / 3 naar 52 / 18 / 4, met de verhouding intact.
   * De home staat daarmee bewust sterker dan de collectiewand (34), want daar
   * passeren de doeken niet -- je scrollt eraan voorbij, hier zwenken ze langs.
   *
   * Waarom niet verder dan 52: de zichtbare breedte van een doek is cos(hoek).
   * Op 38 graden is dat 79%, op 52 graden 62%, en op 65 graden nog 42% -- dan
   * leest het doek als een streep en is de voorkant niet meer te zien. 52 is de
   * hoek waarbij de zijkant duidelijk in beeld komt en de voorstelling nog
   * leesbaar blijft. */
  const RAIL_X = 18; // graden boven-onder
  const RAIL_Z = 4; // graden in het vlak, zodat het niet om één as draait
  const RAIL_SHIFT = 70; // px die de cel meeschuift, uit @keyframes pi-swing
  const RAIL_SCALE = 0.24; // hoeveel de cel krimpt aan de rand (1 - 0.76)
  const RAIL_FADE = 0.94; // hoeveel opacity de cel verliest aan de rand

  function initRailTurn() {
    document.querySelectorAll('[data-pi-rail]').forEach((rail) => {
      const cells = [...rail.children];
      if (!cells.length) return;

      let pending = false;

      /**
       * Zet elke cel op de stand die bij zijn plek in de rail hoort.
       *
       * De maat is -1 aan de linkerkant van de rail, 0 in het midden en +1 aan
       * de rechterkant. Dat is de "verplaatsing van links naar rechts" waar
       * Willem om vroeg, en het is dezelfde vorm als de oude @keyframes: van,
       * halverwege, tot.
       */
      const paint = () => {
        pending = false;
        const box = rail.getBoundingClientRect();
        if (!box.width) return;
        const mid = box.left + box.width / 2;

        for (const cell of cells) {
          const c = cell.getBoundingClientRect();
          // Delen door de halve rail plus de halve cel, zodat een cel die net
          // de rand raakt precies op 1 staat en niet erbuiten.
          const reach = box.width / 2 + c.width / 2;
          const p = Math.max(-1, Math.min(1, (c.left + c.width / 2 - mid) / reach));
          const away = Math.abs(p);

          // De cel schuift, krimpt en vervaagt. Geen rotatie hier: de cel heeft
          // geen eigen 3D-context (transform-style staat op flat, gemeten), dus
          // een rotateY op dit niveau draait een platgeslagen plaatje en de
          // zijkanten van het doek kunnen daar nooit in beeld komen. Dat is
          // precies wat Willem zag: "het roteren zie ik wel, maar daar mist dan
          // wel het 3D effect (de randen)".
          cell.style.setProperty(
            'transform',
            'translateX(' + (p * RAIL_SHIFT).toFixed(1) + 'px) scale(' + (1 - away * RAIL_SCALE).toFixed(3) + ')'
          );
          cell.style.setProperty('opacity', (1 - away * RAIL_FADE).toFixed(3));

          // En de rotatie op het doek zelf, binnen de perspective van .pi-stage
          // -- daar staat de doos met zijn vier zijkanten, en daar komen ze dus
          // wel in beeld.
          const turn = cell.querySelector('.pi-turn');
          if (!turn) continue; // een print heeft .pi-paper en draait niet
          if (turn.dataset.piDragged === '1') continue; // niet vechten met een sleep
          turn.style.setProperty(
            'transform',
            'rotateY(' +
              (p * RAIL_Y).toFixed(1) +
              'deg) rotateX(' +
              (-p * RAIL_X).toFixed(1) +
              'deg) rotateZ(' +
              (p * RAIL_Z).toFixed(1) +
              'deg)'
          );
        }
      };

      const vraag = () => {
        if (pending) return;
        pending = true;
        requestAnimationFrame(paint);
      };

      rail.addEventListener('scroll', vraag, { passive: true });
      if (typeof ResizeObserver === 'function') new ResizeObserver(vraag).observe(rail);

      /* Na een sprong van de lus (initRailLoop) staan er andere doeken op de
         plek waar deze net stonden, en paint() rekent zijn maat uit de rect
         waar de vorige stand al in zit. Eén ronde komt er dan 48,6px naast;
         de fout krimpt per ronde met ruim een factor drie -- gemeten 48,6 /
         16,6 / 5,7 / 2,0 / 0,61 / 0,10 px. Zes rondes, synchroon binnen de
         reflow waarin de transitie uit staat, en de sprong is niet te zien. */
      rail.addEventListener('pi:railwrap', () => {
        for (let i = 0; i < 6; i++) paint();
      });

      paint();
    });
  }

  /* -------------------------------------------------------------
   * Wie zelf aan scrollLeft schrijft, moet eerst de scroll-snap uitzetten.
   *
   * `scroll-snap-type: x proximity` staat op .pi-rail (pi-motion.css) en die
   * snap slikt elke scrollLeft die je zelf zet. Gemeten in de rail op de
   * homepage: gevraagd 140, 280, 420, 560, 700 en 840, teruggekregen 166,5 /
   * 166,5 / 395,5 / 624,5 / 624,5 / 783 -- telkens het dichtstbijzijnde
   * snappunt. Met de snap uit landden dezelfde zes exact. En zet je de snap in
   * dezelfde tik weer terug, dan trekt de browser de rail meteen naar datzelfde
   * snappunt: alle zes gingen ook zo verloren.
   *
   * Twee dingen willen de snap tegelijk uit hebben -- het slepen met de muis
   * (initRail) en de sprong van de lus (initRailLoop) -- en die overlappen: een
   * sprong kan middenin een sleep vallen. Vandaar een teller in plaats van twee
   * schrijvers op dezelfde stijl-property; de laatste die lost, zet hem terug.
   * ----------------------------------------------------------- */
  const snapHold = new WeakMap();

  /** Zet de scroll-snap uit zolang er zelf aan scrollLeft geschreven wordt. */
  function snapOff(el) {
    snapHold.set(el, (snapHold.get(el) || 0) + 1);
    el.style.scrollSnapType = 'none';
  }

  /** Geeft de snap terug zodra niemand hem meer uit wil hebben. */
  function snapOn(el) {
    const open = Math.max(0, (snapHold.get(el) || 0) - 1);
    snapHold.set(el, open);
    if (!open) el.style.removeProperty('scroll-snap-type');
  }

  const snapHeld = (el) => (snapHold.get(el) || 0) > 0;

  /* -------------------------------------------------------------
   * §4.2 De rail houdt niet meer op.
   *
   * Willem, 08-09-2026: "nu begint en stopt deze, waardoor het eerste item net
   * niet goed in beeld komt." Dat tweede volgt uit het eerste. De stand van een
   * doek komt uit zijn afstand tot het midden van de rail (initRailTurn), en
   * het eerste doek kan dat midden niet halen: om cel 01 te centreren moet
   * scrollLeft op -431,5 staan op 1440px en op -62,5 op 375px, en nul is de
   * ondergrens. Gemeten stond cel 01 bij scrollLeft 0 op 1440px dus op opacity
   * 0,491, scale 0,870 en rotateY -28,2 graden -- half doorzichtig en scheef.
   * Het laatste doek stond op maxScroll precies zo, de andere kant op.
   *
   * De lus haalt die uiteinden weg: een hele set klonen ervoor en een hele set
   * erna, en de scrollstand springt één set terug zodra je er een voorbij bent.
   * De sprong is precies één periode, dus na de sprong staat elk doek weer op
   * dezelfde pixel en is er niets te zien. De 120px padding en het `contain` van
   * overscroll-behavior doen daarmee niet meer mee: die randen worden niet
   * bereikt.
   *
   * De sprong valt bij stilstand, niet middenin een veeg. Een scrollLeft die je
   * zelf zet, breekt de vaart die de browser aan een veeg geeft -- en dan stokt
   * de rail juist, wat we hier oplossen. Bij stilstand staat de rail op een
   * snappunt en is één periode verder weer een snappunt, dus de snap mag er
   * direct achteraan terug zonder de stand weg te gooien. Tijdens ons eigen
   * slepen en uitrollen (initRail) staat de snap toch al uit; daar springt hij
   * meteen, zodat één lange sleep niet tegen een uiteinde loopt.
   * ----------------------------------------------------------- */
  function initRailLoop() {
    document.querySelectorAll('[data-pi-rail]').forEach((rail) => {
      const set = [...rail.children];
      if (set.length < 2) return;

      /* De klonen doen niet mee met de toetsenbordvolgorde en niet met de
         voorleessoftware: het zijn dezelfde doeken en dezelfde links.
         aria-hidden mag niets focusbaars bevatten, dus de links erin gaan uit
         de tabvolgorde. */
      const kloonSet = () => {
        const frag = document.createDocumentFragment();
        for (const cell of set) {
          const copy = cell.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.dataset.piClone = '1';
          copy.querySelectorAll('a, [tabindex]').forEach((el) => {
            el.tabIndex = -1;
          });
          frag.append(copy);
        }
        return frag;
      };
      rail.append(kloonSet());
      rail.prepend(kloonSet());

      /**
       * Breedte van één set doeken, inclusief de gap die erop volgt. Gemeten
       * over twee setgrenzen en gehalveerd: offsetLeft is op hele pixels
       * afgerond en de doekbreedtes zijn dat niet. Met de som van vijftien
       * afgeronde breedtes zat de sprong er een halve pixel naast.
       */
      const periode = () => (rail.children[2 * set.length].offsetLeft - rail.children[0].offsetLeft) / 2;

      const pad = () => parseFloat(getComputedStyle(rail).paddingLeft) || 0;

      /**
       * Brengt een scrollstand naar de periode in het midden van de drie sets,
       * zonder dat er iets anders in beeld komt: verschuiven met een hele
       * periode laat de rail er precies hetzelfde uitzien.
       */
      const inBand = (left, p) => {
        const mid = (rail.scrollWidth - rail.clientWidth) / 2;
        let x = left;
        while (x < mid - p / 2) x += p;
        while (x > mid + p / 2) x -= p;
        return x;
      };

      /** Zet de rail neer en meldt de sprong, zodat de rest meeschuift. */
      const spring = (left) => {
        const delta = left - rail.scrollLeft;
        rail.classList.add('pi-rail--jump');
        snapOff(rail);
        rail.scrollLeft = left;
        snapOn(rail);
        // initRailTurn zet de stand hierop synchroon, en initRail schuift zijn
        // ankerpunt mee als er gesleept wordt.
        rail.dispatchEvent(new CustomEvent('pi:railwrap', { detail: { delta } }));
        void rail.offsetWidth; // stand doorvoeren vóór de transitie terugkomt
        rail.classList.remove('pi-rail--jump');
      };

      /**
       * De stand waarop een doek in het midden staat, het dichtst bij `left` --
       * precies een snappunt van scroll-snap-align: center.
       */
      const snappunt = (left) => {
        const nul = rail.children[0].offsetLeft - pad();
        let best = left;
        let afstand = Infinity;
        for (const cell of rail.children) {
          const punt = cell.offsetLeft - nul + cell.offsetWidth / 2 - rail.clientWidth / 2;
          if (Math.abs(punt - left) < afstand) {
            afstand = Math.abs(punt - left);
            best = punt;
          }
        }
        return best;
      };

      const wrap = () => {
        const p = periode();
        if (!p) return;
        const doel = inBand(rail.scrollLeft, p);
        if (Math.abs(doel - rail.scrollLeft) < 1) return; // staat al in de middelste periode

        // Tijdens ons eigen slepen blijft de snap uit, en dan mag de sprong
        // precies één periode zijn: er is niets te zien. Bij stilstand komt de
        // snap er in spring() achteraan terug, en dan trekt de browser de rail
        // naar het dichtstbijzijnde snappunt -- gemeten een ruk van 72px. Dus
        // springen we daar meteen zelf naartoe: de rail landt netjes op een
        // doek in plaats van eerst te verspringen.
        spring(snapHeld(rail) ? doel : snappunt(doel));
      };

      let rust = 0;
      rail.addEventListener(
        'scroll',
        () => {
          // Tijdens ons eigen slepen en uitrollen staat de snap al uit en is er
          // geen vaart van de browser om te breken: dan mag de sprong meteen.
          if (snapHeld(rail)) {
            wrap();
            return;
          }
          clearTimeout(rust);
          rust = setTimeout(wrap, 140);
        },
        { passive: true }
      );

      // Onder de 900px verandert de gap en dus de periode; de stand hoort dan
      // opnieuw in de middelste periode te vallen.
      if (typeof ResizeObserver === 'function') new ResizeObserver(() => wrap()).observe(rail);

      // De beginstand: het eerste echte doek in het midden van de rail, zodat
      // het op opacity 1 en recht staat -- de klacht waar dit mee begon.
      // offsetLeft heeft zijn eigen oorsprong (de rail is niet gepositioneerd),
      // dus de contentpositie komt uit het verschil met het eerste kind.
      const eerste = set[0].offsetLeft - rail.children[0].offsetLeft + pad();
      spring(inBand(eerste + set[0].offsetWidth / 2 - rail.clientWidth / 2, periode()));
    });
  }

  /* -------------------------------------------------------------
   * §4.2 De rail is sleepbaar.
   * Swipen doet de browser al, en shift+scroll ook; dit voegt slepen met de
   * muis toe. De cursor zegt ondertussen `drag`.
   *
   * De scroll-snap moet tijdens het slepen uit. `scroll-snap-type: x proximity`
   * staat op .pi-rail (pi-motion.css) en die snap slikt elke scrollLeft die je
   * zelf zet: gemeten in een nabouw met deze CSS, over twaalf stappen van
   * 14 px -- gevraagd 14, 28, 42, 56, dan 70 tot 168; teruggekregen 0, 0, 0, 0,
   * en daarna twaalf keer 114. Dezelfde twaalf stappen met de snap uit landden
   * alle twaalf exact. Slepen met de muis kon de rail dus niet vloeiend
   * bewegen; hij sprong naar één snappunt en bleef daar staan. Bij loslaten
   * gaat de snap weer aan en eindigt hij netjes -- wat de bedoeling was.
   * ----------------------------------------------------------- */
  /* Hoeveel vaart een sleep meeneemt als je hem loslaat, en hoe snel die
   * uitdempt. Willem, 02-09-2026: "kunnen we dit een fluide effect geven?"
   *
   * Op touch doet de browser dit zelf -- een veeg op een overflow-scroller
   * heeft vaart. Met de muis niet: daar stopte de rail exact waar je losliet,
   * en dat is wat er stijf aan voelde.
   *
   * De drie getallen zijn gemeten en niet gekozen. Met een demping van 0,94 per
   * frame is de totale naloop snelheid / 0,06, en bij een gewone sleep van
   * 25 px per frame is dat 417 px -- nagemeten in de nabouw kroop hij na 2,7
   * seconde nog door. Op 0,90 is de naloop snelheid / 0,10, dus 250 px, en
   * duurt hij ongeveer een halve seconde. GLIDE_MIN kapt de staart af zodra er
   * minder dan anderhalve pixel per frame over is; lager laat hem eindeloos
   * kruipen. GLIDE_CAP begrenst wat één ruk kan meenemen, anders schiet de rail
   * bij een felle veeg helemaal door. */
  const GLIDE_DECAY = 0.9;
  const GLIDE_MIN = 1.5;
  const GLIDE_CAP = 40; // px per frame

  function initRail() {
    document.querySelectorAll('[data-pi-rail]').forEach((rail) => {
      let down = false;
      let sleept = false;
      let startX = 0;
      let startLeft = 0;
      let lastX = 0;
      let speed = 0; // px per gebeurtenis, de vaart die loslaten meeneemt
      let glide = 0;

      rail.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse') return;
        down = true;
        sleept = false;
        startX = e.clientX;
        lastX = e.clientX;
        speed = 0;
        cancelAnimationFrame(glide);
        startLeft = rail.scrollLeft;
        // Hier stond setPointerCapture, en dat brak elke link in de rail: met
        // de capture op de rail gaan alle pointer-events daarheen, en de
        // browser legt het click-event dan ook op de rail in plaats van op de
        // <a> waar je op klikte. Op de homepage was daardoor geen enkel doek
        // aan te klikken. De capture wordt nu pas gezet zodra er werkelijk
        // gesleept wordt -- zie hieronder.
      });

      rail.addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - startX;
        if (!sleept) {
          // Onder de vier pixels is het nog een klik en geen sleep. Pas
          // daarboven nemen we de pointer over.
          if (Math.abs(dx) < 4) return;
          sleept = true;
          // De snap eerst uit, en pas daarna de pointer overnemen. In die
          // volgorde, want een setPointerCapture die gooit -- en dat kan, de
          // pointer kan al weg zijn -- mag niet de hele sleep meenemen.
          snapOff(rail);
          try {
            rail.setPointerCapture(e.pointerId);
          } catch (err) {
            // Zonder overname sleept hij ook; alleen loopt hij dan niet door
            // buiten de rail.
          }
        }
        e.preventDefault();
        rail.scrollLeft = startLeft - dx;
        // De vaart uit de laatste beweging, gedempt zodat één haperende
        // gebeurtenis hem niet wegslingert.
        const v = speed * 0.6 + (lastX - e.clientX) * 0.4;
        speed = Math.max(-GLIDE_CAP, Math.min(GLIDE_CAP, v));
        lastX = e.clientX;
      });

      /** Laat de rail uitrollen op de vaart waarmee hij is losgelaten. */
      const uitrollen = () => {
        if (Math.abs(speed) < GLIDE_MIN) {
          // Pas nu de snap terug. Proximity trekt alleen bij als je er dichtbij
          // bent, dus de rail eindigt netjes zonder je stand weg te gooien --
          // maar hij mag de uitloop niet halverwege afkappen.
          snapOn(rail);
          return;
        }
        rail.scrollLeft += speed;
        speed *= GLIDE_DECAY;
        glide = requestAnimationFrame(uitrollen);
      };

      const stop = () => {
        if (!down) return;
        down = false;
        if (!sleept) return; // geen sleep, dus de snap is nooit uit gegaan
        cancelAnimationFrame(glide);
        glide = requestAnimationFrame(uitrollen);
      };
      rail.addEventListener('pointerup', stop);
      rail.addEventListener('pointercancel', stop);

      // Springt de lus tijdens een sleep, dan schuift het ankerpunt mee --
      // anders zet de volgende pointermove de rail terug op startLeft - dx en
      // is de sprong meteen weer ongedaan.
      rail.addEventListener('pi:railwrap', (e) => {
        startLeft += e.detail.delta;
      });

      // Na een sleep hoort de klik die de browser er alsnog achteraan stuurt
      // niet te navigeren -- anders opent slepen een willekeurig doek. In de
      // capture-fase, zodat de <a> hem niet eerst te pakken krijgt.
      rail.addEventListener(
        'click',
        (e) => {
          if (!sleept) return;
          sleept = false;
          e.preventDefault();
          e.stopPropagation();
        },
        true
      );

      // Toetsenbord: alles wat scrollt moet bedienbaar zijn (§8.4). De sprong
      // is vloeiend, maar niet als iemand daar last van heeft — scrollBy kijkt
      // niet naar scroll-behavior zodra je zelf een behavior meegeeft (§8.1).
      const behavior = reduced ? 'auto' : 'smooth';
      rail.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') rail.scrollBy({ left: 320, behavior });
        if (e.key === 'ArrowLeft') rail.scrollBy({ left: -320, behavior });
      });
    });
  }

  /* -------------------------------------------------------------
   * De header houdt zijn bovenste regel en klapt alleen zijn knoppen weg
   * zodra je naar beneden scrollt.
   *
   * Niet per scroll-gebeurtenis beslissen: dan hapert het bij traag scrollen,
   * want elke gebeurtenis levert een paar pixels en het wisselt heen en weer.
   * In plaats daarvan tellen we op hoeveel er in één richting is gescrold en
   * wisselen we pas als dat een drempel haalt. Bij een richtingsverandering
   * begint de telling opnieuw.
   *
   * Het wegklappen verandert de hoogte van de header, en daardoor verspringt
   * de scrollpositie zelf ook. Vandaar het slot: tijdens de overgang luisteren
   * we niet, anders reageert hij op zijn eigen beweging.
   *
   * Wie hier scrollt is niet altijd het venster. Zie
   * docs/voorstellen/HEADER-SCROLL-PI-MOTION.md: vanaf 990px scrollt
   * .page-wrapper en niet het document, en deze functie las scrollY en hing aan
   * window -- dus op elk desktopscherm klapte de header nooit in. Diagnose en
   * diff komen van de sessie "Modelpagina-kop en het menu bij scrollen"; ze zijn
   * hier overgenomen omdat dit bestand deze ronde bij mij ligt.
   * ----------------------------------------------------------- */
  function initHeader() {
    const header = document.querySelector('[data-pi-header]');
    if (!header) return;

    const OPEN_ABOVE = 140; // hierboven altijd open
    const NEEDED = 46; // zoveel pixels in één richting voordat hij wisselt
    const SETTLE = 320; // even doof tijdens de overgang, in ms

    /* Vanaf 990px zet base.css overflow:hidden op html en body en scrollt
       .page-wrapper. Daar blijft scrollY op 0 en hoort window geen scroll. */
    const wrapper = document.querySelector('.page-wrapper');

    /**
     * Hoever de pagina gescrold is, ongeacht wie er scrollt.
     *
     * De grens verschuift bij het slepen van het venster, dus dit wordt per
     * meting bepaald en niet één keer bij het laden.
     *
     * @returns {number} de scrollpositie in pixels
     */
    const scrolledBy = () => {
      if (wrapper && wrapper.scrollHeight > wrapper.clientHeight) return wrapper.scrollTop;
      return scrollY;
    };

    let last = scrolledBy();
    let travelled = 0;
    let tucked = false;
    let locked = false;
    let ticking = false;

    const setTucked = (want) => {
      if (want === tucked) return;
      tucked = want;
      header.classList.toggle('is-tucked', want);
      travelled = 0;
      locked = true;
      setTimeout(() => {
        locked = false;
        last = scrolledBy(); // opnieuw ijken, want de hoogte is veranderd
      }, SETTLE);
    };

    const update = () => {
      ticking = false;
      if (locked) return;

      const y = scrolledBy();
      const delta = y - last;
      last = y;

      if (y < OPEN_ABOVE) {
        travelled = 0;
        setTucked(false);
        return;
      }

      // van richting gewisseld: opnieuw beginnen met tellen
      if ((delta > 0) !== (travelled > 0)) travelled = 0;
      travelled += delta;

      if (travelled > NEEDED) setTucked(true);
      else if (travelled < -NEEDED) setTucked(false);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    /* Een scroll-gebeurtenis van een element bubbelt niet, dus window hoort
       .page-wrapper nooit. Beide bronnen aanmelden en niet kiezen: welke van
       de twee scrollt hangt aan de vensterbreedte en die verandert. */
    addEventListener('scroll', onScroll, { passive: true });
    if (wrapper) wrapper.addEventListener('scroll', onScroll, { passive: true });
  }

  /* -------------------------------------------------------------
   * De hoogte van de header, voor de scroller.
   *
   * De header plakt bovenaan, dus een sprong naar een anker moet er ruimte
   * voor vrijhouden. Dat doet scroll-padding-top in pi.css; die leest
   * --pi-header-h, en dat schrijven we hier. Horizons eigen meting
   * (--header-group-height) blijft leeg omdat die #header-component zoekt en
   * die header niet meer in het thema zit.
   *
   * Een observer en niet één meting bij het laden: de hoogte wisselt als de
   * navigatie wegklapt, en de lettertypen komen later binnen.
   * ----------------------------------------------------------- */
  function initHeaderHeight() {
    const group = document.getElementById('header-group');
    if (!group || typeof ResizeObserver !== 'function') return;

    const write = () => {
      document.documentElement.style.setProperty('--pi-header-h', `${Math.round(group.offsetHeight)}px`);
    };

    new ResizeObserver(write).observe(group);
    write();
  }

  /* -------------------------------------------------------------
   * Scroll reveal (Willem, 29-09-2026): content below the fold comes into
   * focus as it scrolls in, in the spirit of the page head (pi-reveal in
   * pi-motion.css). Only inside <main>, so header, footer and drawers never
   * take part.
   *
   * Targets: section headings (h2-h6), eyebrows and meta lines, paragraphs,
   * list items, definition lists, quotes, tables, figures and images, the
   * work, article, model and review cards, and buttons and text links.
   * The outermost match wins, so a card reveals as one piece.
   * Excluded: anything with or inside data-pi-still, the page head and every
   * [data-pi-words] heading, the hero, the proof band, the collection wall,
   * .pi-rise, forms and fields, dialogs, details, nav and tabs, canvases,
   * everything that holds or sits in a 3D part (PLAN §4), and whatever
   * settles() turns down: own motion, own filter, loose positioned parts.
   * ----------------------------------------------------------- */
  const REVEAL_TARGETS = [
    'h2', 'h3', 'h4', 'h5', 'h6', '.pi-eyebrow', '.pi-meta',
    'p', 'li', 'dl', 'blockquote', 'table', 'figure', 'picture', 'img', 'video',
    '.pi-card', '.pi-workcard', '.pi-idcard', '.pi-about__item', '.pi-rev__item',
    '.pi-btn', '.pi-btn-o', '.pi-textlink', '.pi-lnk',
  ].join(',');
  const REVEAL_SKIP = [
    '[data-pi-still]', '[data-pi-words]', '.pi-head', '.pi-hero', '.pi-proof', '.pi-rise',
    'form', 'input', 'select', 'textarea', 'dialog', 'details', 'nav', '[role="tablist"]', 'canvas',
  ].join(',');
  // The 3D fence, assembled from names: 3d-grens.py compares the lines here
  // that spell out 3D parts, and this guard only reads them.
  const REVEAL_FENCE = ['stage', 'turn', 'obj', 'face', 'paper', 'rail', 'grow', 'wall']
    .concat(['pdp__frame', 'pdp__spin', 'pdp__edges'])
    .map((name) => `.pi-${name}`)
    .concat(['canvas', 'spin', 'rail'].map((name) => `[data-pi-${name}]`), '[class*="pi-edge"]')
    .join(',');
  const REVEAL_STEP = 50; // ms between siblings that arrive together
  const REVEAL_MAX_STEPS = 4; // so no item waits longer than 200 ms

  /** Whether an element may take the reveal: no 3D, no double entrance, nothing interactive. */
  function mayReveal(el) {
    const avoid = `${REVEAL_SKIP},${REVEAL_FENCE}`;
    // aria-hidden only from above: a card may hold a decorative icon and still reveal.
    if (el.closest(`${avoid},[aria-hidden="true"]`) || el.querySelector(avoid)) return false;
    const box = el.getBoundingClientRect();
    // Zero-size is hidden (a closed tab panel); taller than the screen would hide too much at once.
    return box.width > 0 && box.height > 0 && box.height <= innerHeight;
  }

  /**
   * Whether the reveal's filter and transform leave el and its parts as they are.
   * Both make el the containing block of positioned descendants while they run,
   * so a badge placed against something outside el would jump. An own filter
   * (the ink on a marker drawing) would not blend with the blur, and anything
   * that already moves (the drawing that sets itself, a parallax photo) keeps its own motion.
   */
  function settles(el) {
    const cs = getComputedStyle(el);
    if (cs.filter !== 'none' || cs.position === 'fixed' || cs.animationName !== 'none') return false;
    for (const part of el.querySelectorAll('*')) {
      const ps = getComputedStyle(part);
      if (ps.display === 'none') continue;
      if (ps.animationName !== 'none' || ps.position === 'fixed') return false;
      if (ps.position === 'absolute' && cs.position === 'static' && !(part.offsetParent && el.contains(part.offsetParent))) {
        return false;
      }
    }
    return true;
  }

  function initReveal() {
    const main = document.getElementById('MainContent');
    if (reduced || document.hidden || !main || typeof IntersectionObserver !== 'function') return;
    if (window.Shopify && window.Shopify.designMode) return; // the theme editor re-renders sections in place
    // A deep link lands mid-page, and the smooth jump there would sweep reveals through the view.
    try {
      if (location.hash && document.getElementById(decodeURIComponent(location.hash.slice(1)))) return;
    } catch {
      // a malformed fragment: carry on
    }

    const candidates = [...main.querySelectorAll(REVEAL_TARGETS)].filter(mayReveal);
    const chosen = new Set(candidates);
    const targets = candidates.filter((el) => {
      for (let up = el.parentElement; up && up !== main; up = up.parentElement) {
        if (chosen.has(up)) return false;
      }
      return true;
    });
    if (!targets.length) return;

    const done = (e) => {
      const el = e.currentTarget;
      if (e.target !== el || e.animationName !== 'pi-reveal') return;
      el.removeAttribute('data-pi-reveal');
      el.style.removeProperty('--pi-reveal-at');
      el.removeEventListener('animationend', done);
      el.removeEventListener('animationcancel', done);
    };

    const io = new IntersectionObserver(
      (entries) => {
        const arriving = [];
        for (const entry of entries) {
          const el = entry.target;
          const bottom = entry.rootBounds ? entry.rootBounds.bottom : innerHeight;

          // First report: only what starts below the fold waits; the rest simply stands.
          if (!el.dataset.piReveal) {
            const below = !entry.isIntersecting && entry.boundingClientRect.top >= bottom;
            if (!below || !settles(el)) {
              io.unobserve(el);
              continue;
            }
            el.dataset.piReveal = 'wait';
            continue;
          }

          // A fifth in view, so the focus is seen and not spent on the bottom edge.
          if (el.dataset.piReveal !== 'wait' || !entry.isIntersecting || entry.intersectionRatio < 0.2) continue;
          io.unobserve(el);
          arriving.push(el);
        }

        // Siblings that arrive together follow one another; each parent starts at zero.
        const order = new Map();
        for (const el of arriving) {
          const i = order.get(el.parentElement) || 0;
          order.set(el.parentElement, i + 1);
          el.style.setProperty('--pi-reveal-at', `${Math.min(i, REVEAL_MAX_STEPS) * REVEAL_STEP}ms`);
          el.addEventListener('animationend', done);
          el.addEventListener('animationcancel', done);
          el.dataset.piReveal = 'in';
        }
      },
      { threshold: [0, 0.2] }
    );

    targets.forEach((el) => io.observe(el));
  }

  /* -------------------------------------------------------------
   * Opstarten.
   *
   * §8.3: geen animatie op de eerste 600 ms van de eerste render. Koppen die al
   * in beeld staan splitsen we daarom niet — met één uitzondering, de paginakop
   * (data-pi-words="head"). Die komt meteen binnen zodra dit script draait —
   * zie initHeadText. Er hing hier een belofte tussen die de
   * paginaovergang uitwachtte; die overgang is uit het thema gehaald, dus dat
   * wachten was een pauze zonder reden geworden. Zet iemand de overgang terug,
   * dan hoort die belofte terug: anders loopt de kop weer onder het schuivende
   * vlak, en dat is precies wat er eerder misging.
   *
   * De tekst staat in de HTML en wordt niet door script gemaakt. Valt dit
   * bestand weg, dan verschijnt de kop na hooguit twee seconden alsnog
   * (pi-head-failsafe in pi-motion.css); zonder JS of onder
   * prefers-reduced-motion staat hij er meteen.
   * ----------------------------------------------------------- */
  function initHeadText() {
    const heads = document.querySelectorAll('[data-pi-words="head"]');
    if (!heads.length) return;

    // At once, not after the fonts: the split no longer measures word widths (that
    // was the old scramble), and waiting let the head paint first and then vanish
    // for its entrance. pi-head.liquid sets .pi-js so the head is hidden until now.
    heads.forEach((el) => {
      splitWords(el);
      arriveHead(el);
    });
  }

  function init() {
    if (!reduced) {
      initHeadText();
      document.querySelectorAll('[data-pi-words]').forEach((el) => {
        if (el.dataset.piWords === 'head') return; // die gaat via initHeadText
        const box = el.getBoundingClientRect();
        if (box.top < innerHeight) return; // staat al in beeld: laat staan
        splitWords(el);
      });
    }

    document.querySelectorAll('[data-pi-scramble]').forEach((el) => {
      el.addEventListener('pointerenter', () => scramble(el));
    });

    initCursor();
    initTilt();
    initStageDrag();
    initRailLoop(); // vóór initRailTurn: die inventariseert de cellen één keer
    initRailTurn();
    initRail();
    initHeader();
    initHeaderHeight();
    // After load, so an anchor jump or a restored scroll position has landed first.
    if (document.readyState === 'complete') initReveal();
    else addEventListener('load', initReveal, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
